import React from 'react';
import {
  Form,
  DatePicker,
  Select,
  Row,
  Col
} from 'antd';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import settings from 'config/settings';
import REDUCER_KEY from 'reducers/reducer.keys';
import { list } from 'actions/index';
import Item from './Item';

const { Option } = Select;

const getSelectedRoster = (
  /** @type {any[]} */ rosterList,
  /** @type {number} */ selectedRosterId
) => rosterList?.find(roster => roster?.id === selectedRosterId);

const getRosterVersionsOptionsForSelectedRoster = (
  /** @type {any[]} */ rosterList,
  /** @type {number} */ selectedRosterId
) => {
  const options = [];
  const selectedRoster = getSelectedRoster(rosterList, selectedRosterId);
  if (selectedRoster && selectedRoster?.versions) {
    for (const {
      id,
      name
    } of selectedRoster.versions) {
      options.push(
        <Option key={id} value={id}>
          {`${id} - ${name}`}
        </Option>
      );
    }
  }
  return options;
};

const mapRosterToOptions = (
  /** @type {{ id: number; name:string; }} */ roster
) => {
  const { id, name } = roster;
  if (id && name) {
    return <Option key={id} value={id}>{name}</Option>;
  }
  return undefined;
};

const getRosterVersionPlaceholder = (
  /** @type {boolean} */ loading,
  /** @type {number} */ rosterId,
  /** @type {boolean} */ noRosterVersions
) => {
  switch (true) {
  case loading:
    return 'Loading...';
  case !rosterId:
    return 'No Roster Selected';
  case noRosterVersions:
    return 'No Roster Versions';
  default:
    return 'Select Roster Version';
  }
};

const getRosterPlaceholder = (
  /** @type {boolean} */ loading,
  /** @type {boolean} */ noRosters
) => {
  switch (true) {
  case loading:
    return 'Loading...';
  case noRosters:
    return 'No Available Rosters';
  default:
    return 'Select Roster';
  }
};

const mapStateToProps = state => ({
  rosterList: state.rosterList
});

const mapDispatchToProps = dispatch => ({
  loadRosterList() {
    const page = 0;
    const pageSize = settings.APP_CONFIG.dropListSize;
    const sorted = [{
      id: 'name',
      desc: false
    }];
    const filters = [{
      id: 'active',
      value: true
    }];
    return dispatch(list(
      REDUCER_KEY.rosters.key, {
        page,
        pageSize,
        filters,
        sorted
      }
    ));
  }
});

class RosterFormInputs extends React.Component {
  isMounted = false;

  static propTypes = {
    loadRosterList: PropTypes.func.isRequired,
    form: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      start_date: moment(),
      roster: undefined,
      roster_version: undefined,
      rosterList: []
    };
  }

  componentDidMount() {
    this.isMounted = true;
    const { loadRosterList } = this.props;

    if (this.isMounted) {
      this.setState({ loading: true });
      loadRosterList()
        .then(
          (/** @type {{ results:[] }} */ response) => {
            this.setState({
              loading: false,
              rosterList: response.results
            }, () => {
              const initialRosterIdValue = this.getDefaultRosterDropdownValue();
              const initialRosterVersionValue = this.getDefaultRosterVersionByRosterId(initialRosterIdValue);
              this.setState({
                roster: initialRosterIdValue,
                roster_version: initialRosterVersionValue
              });
            });
          }
        );
    }
  }

  componentWillUnmount() {
    this.isMounted = false;
  }

  getDefaultRosterDropdownValue = () => {
    const { rosterList } = this.state;
    const [firstRoster] = rosterList;
    return firstRoster?.id;
  }

  getDefaultRosterVersionByRosterId = (/** @type {number} */ rosterId) => {
    const { rosterList } = this.state;
    const selectedRoster = getSelectedRoster(rosterList, rosterId);
    const [firstVersion] = selectedRoster?.versions;
    return firstVersion?.id;
  }

  handleSelectOptionFilter = (input, option) => {
    const optionValue = String(option.props.children).toLowerCase();
    const inputValue = String(input).toLowerCase();
    return optionValue.indexOf(inputValue) >= 0;
  };

  handleRosterDropdownChange = (/** @type {number} */ rosterId) => {
    const { form } = this.props;
    this.setState({ roster: rosterId }, () => {
      const rosterVersionId = this.getDefaultRosterVersionByRosterId(rosterId);
      form.setFieldsValue({ roster_version: rosterVersionId });
      const rosterVersionInputInstance = form.getFieldInstance('roster_version');
      rosterVersionInputInstance.focus();
    });
  };

  handleRosterVersionDropdownChange = (/** @type {number} */ rosterVersionId) => {
    this.setState({ roster_version: rosterVersionId });
  };

  handleStartDateChange = (/** @type {moment} */ startDate) => {
    this.setState({ start_date: startDate }, () => {
      const { form } = this.props;
      const rosterInputInstance = form.getFieldInstance('roster');
      rosterInputInstance.focus();
    });
  };

  render() {
    const { form } = this.props;
    const {
      rosterList, loading, start_date, roster, roster_version
    } = this.state;
    const { getFieldDecorator } = form;

    // Roster Select Input
    const rosterOptions = rosterList.map(mapRosterToOptions);
    const noRosters = rosterList.length < 1;
    const rosterPlaceholder = getRosterPlaceholder(loading, noRosters);
    const rosterDisabled = loading || noRosters;

    // Roster Versions Select Input
    const rosterVersionOptions = getRosterVersionsOptionsForSelectedRoster(rosterList, roster);
    const noRosterVersions = rosterVersionOptions.length < 1;
    const rosterVersionPlaceholder = getRosterVersionPlaceholder(loading, roster, noRosterVersions);
    const rosterVersionDisabled = loading || !roster || noRosterVersions || noRosters;

    return (
      <Row type="flex" justify="start" align="middle" style={{ width: '100%' }}>
        <Col span={7}>
          <Item display_name="Start Date" key="start_date" inputFormItemLayout={{}}>
            {getFieldDecorator('start_date', { initialValue: start_date })(
              <DatePicker
                style={{ minWidth: 170 }}
                showTime={false}
                format="DD-MM-YYYY"
                disabled={false}
                onChange={this.handleStartDateChange}
              />
            )}
          </Item>
        </Col>
        <Col span={7}>
          <Item display_name="Roster" key="roster" inputFormItemLayout={{}}>
            {getFieldDecorator('roster', { initialValue: roster ?? undefined })(
              <Select
                autoFocus
                style={{ minWidth: 170 }}
                showSearch
                disabled={rosterDisabled}
                placeholder={rosterPlaceholder}
                defaultActiveFirstOption
                filterOption={this.handleSelectOptionFilter}
                optionFilterProp="children"
                notFoundContent="No Results"
                onChange={this.handleRosterDropdownChange}
              >
                {rosterOptions}
              </Select>
            )}
          </Item>
        </Col>
        <Col span={7}>
          <Item display_name="Roster Version" key="roster_version" inputFormItemLayout={{}}>
            {getFieldDecorator('roster_version', { initialValue: roster_version })(
              <Select
                style={{ minWidth: 170 }}
                showSearch
                placeholder={rosterVersionPlaceholder}
                defaultActiveFirstOption
                filterOption={this.handleSelectOptionFilter}
                optionFilterProp="children"
                notFoundContent="No Results"
                disabled={rosterVersionDisabled}
                onChange={this.handleRosterVersionDropdownChange}
              >
                {rosterVersionOptions}
              </Select>
            )}
          </Item>
        </Col>
        <Col span={3} />
      </Row>
    );
  }
}

const RosterReportInputs = connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { withRef: true }
)(Form.create({})(RosterFormInputs));

export default RosterReportInputs;
